import { Component } from 'preact';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED,
  INPUT_STYLE, FourCorners, GOLD_BTN,
  TAB_ACTIVE_TEXT, TAB_INACTIVE, TAB_BAR_STYLE,
  TEXTURED_PANEL_STYLE, DIVIDER_GRADIENT,
} from '../../lib/medievalTheme';
import { TabBar } from '../ui/TabBar';
import { SorceryElementIcon } from '../CardFilterBar';
import DeckCardTile from '../DeckCardTile';
import CardGrid from '../CardGrid';
import CardInspector from '../CardInspector';
import { isFoilFinish } from '../../utils/sorcery/foil';
import { foilZonesForCard } from '../../utils/sorcery/foilZones';
import { InspectorHoldTracker } from '../../utils/inspectorHold';
import { CloseButton } from '../ui/navButtons';
import {
  CARD_SORT_OPTIONS, CARD_SORT_PREF_KEY, CARD_SORT_DIR_PREF_KEY,
  CARD_SORT_BY_COST, CARD_SORT_DIR_ASC, CARD_SORT_DIR_DESC,
  normalizeCardSort, normalizeCardSortDir, makeCardComparator,
} from '../../utils/sorcery/cardSort';
import { getPref, setPref } from '../../utils/persistentPrefs';
import { pushDismiss, removeDismiss } from '../../utils/escStack';
import { k, t } from '../../localization/t';

const TYPES = [k('Avatar'), k('Minion'), k('Magic'), k('Aura'), k('Artifact'), k('Site')];
const ELEMENTS = [k('Water'), k('Earth'), k('Fire'), k('Air')];

// Full-bleed 1px rule with the shared gold fade so section breaks read
// as etched seams instead of flat hairline borders.
function Divider() {
  return <div style={{ height: '1px', background: DIVIDER_GRADIENT, flexShrink: 0 }} />;
}

/**
 * Pile search dialog used by Spellbook / Atlas / Cemetery hotkeys (X / Z / C).
 * Fixed size, identical layout for all three piles. Renders cards through
 * DeckCardTile so the visual matches the deck builder card grid.
 *
 * Pure presentational. Looks up full sorcery card metadata via the
 * sorceryCards prop because pile cards only carry id / cardId / imageUrl /
 * foiling. DeckCardTile needs the full card object for type filters,
 * elements, etc.
 */
export default class PileSearchDialog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      typeFilters: new Set(),
      elementFilters: new Set(),
      hoveredEntry: null,
      inspectedEntry: null,
      sortBy: normalizeCardSort(getPref(CARD_SORT_PREF_KEY, 'name')),
      sortDir: normalizeCardSortDir(getPref(CARD_SORT_DIR_PREF_KEY, CARD_SORT_DIR_ASC)),
    };
    this.inspectorHold = new InspectorHoldTracker();
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('keyup', this.handleKeyUp);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('keyup', this.handleKeyUp);
    removeDismiss(this._escDismissId);
  }

  componentDidUpdate(_prevProps, prevState) {
    // Space-was-armed → hover landed on a card → open inspector now.
    if (
      !this.state.inspectedEntry &&
      this.state.hoveredEntry &&
      this.state.hoveredEntry !== prevState.hoveredEntry &&
      this.inspectorHold.consumePending()
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ inspectedEntry: this.state.hoveredEntry });
    }

    const open = !!this.state.inspectedEntry;
    const wasOpen = !!prevState.inspectedEntry;
    if (open && !wasOpen) this._escDismissId = pushDismiss(() => this.setState({ inspectedEntry: null }));
    else if (!open && wasOpen) removeDismiss(this._escDismissId);
  }

  // Space toggles the full-screen card inspector for the currently
  // hovered card. Escape closes it. Typing into the search input is
  // ignored so the user can still hit space inside the search field.
  handleKeyDown = (e) => {
    if (!this.props.pile) return;
    const tag = e.target?.tagName;
    if (tag === 'BUTTON' && (e.key === 'Enter' || e.key === ' ')) return;
    const editable = e.target?.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
    if ((e.key === ' ' || e.code === 'Space') && !editable) {
      e.preventDefault();
      // OS key-repeat fires while Space is held. Only act on the
      // first keydown so hold-to-inspect stays open until keyup.
      if (e.repeat) return;
      if (this.state.inspectedEntry) {
        this.inspectorHold.noteClose();
        this.setState({ inspectedEntry: null });
      } else if (this.state.hoveredEntry) {
        this.inspectorHold.noteOpen();
        this.setState({ inspectedEntry: this.state.hoveredEntry });
      } else {
        // No card hovered yet. Arm so the next hover opens.
        this.inspectorHold.noteArmed();
      }
      return;
    }
  };

  // Releasing Space closes the inspector only if it was held past the
  // hold threshold; quick taps leave it open (tap-toggle mode).
  handleKeyUp = (e) => {
    if (!this.props.pile) return;
    if (e.key !== ' ' && e.code !== 'Space') return;
    // Tracker first: releasing Space also disarms a hover-less press.
    if (!this.inspectorHold.shouldCloseOnKeyUp() || !this.state.inspectedEntry) return;
    e.preventDefault();
    this.setState({ inspectedEntry: null });
  };

  toggleType = (type) => {
    this.setState((s) => {
      const next = new Set(s.typeFilters);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return { typeFilters: next };
    });
  };

  clearTypes = () => this.setState({ typeFilters: new Set() });

  toggleElement = (element) => {
    this.setState((s) => {
      const next = new Set(s.elementFilters);
      if (next.has(element)) next.delete(element);
      else next.add(element);
      return { elementFilters: next };
    });
  };

  clearElements = () => this.setState({ elementFilters: new Set() });

  handleSortByChange = (value) => {
    const next = normalizeCardSort(value);
    // Name never toggles direction. It's always A→Z. Cost flips between
    // ascending and descending each time the chip is re-clicked.
    if (next !== CARD_SORT_BY_COST) {
      if (next === this.state.sortBy && this.state.sortDir === CARD_SORT_DIR_ASC) return;
      setPref(CARD_SORT_PREF_KEY, next);
      setPref(CARD_SORT_DIR_PREF_KEY, CARD_SORT_DIR_ASC);
      this.setState({ sortBy: next, sortDir: CARD_SORT_DIR_ASC });
      return;
    }
    if (next === this.state.sortBy) {
      const nextDir = this.state.sortDir === CARD_SORT_DIR_ASC
        ? CARD_SORT_DIR_DESC
        : CARD_SORT_DIR_ASC;
      setPref(CARD_SORT_DIR_PREF_KEY, nextDir);
      this.setState({ sortDir: nextDir });
      return;
    }
    setPref(CARD_SORT_PREF_KEY, next);
    setPref(CARD_SORT_DIR_PREF_KEY, CARD_SORT_DIR_ASC);
    this.setState({ sortBy: next, sortDir: CARD_SORT_DIR_ASC });
  };

  // Build a DeckCardTile-compatible entry from a pile cardInstance.
  // Looks up the full sorcery card by cardId and uses the cardInstance's
  // imageUrl + foiling for the printing. Custom-art tokens from the
  // cosmetics store aren't in the Sorcery card db, so we synthesize a
  // minimal card object from the instance's own fields when the lookup
  // misses but the instance carries enough to render a tile.
  buildEntry = (cardInstance, idx) => {
    let card = this.props.sorceryCards?.find((c) => c.unique_id === cardInstance.cardId);
    if (!card && cardInstance.name && cardInstance.imageUrl) {
      card = {
        unique_id: cardInstance.cardId,
        name: cardInstance.name,
        type: cardInstance.type || 'Token',
        printings: [{
          unique_id: cardInstance.printingId || cardInstance.cardId,
          image_url: cardInstance.imageUrl,
          foiling: cardInstance.foiling || 'S',
        }],
      };
    }
    if (!card) return null;
    const printing = card.printings?.find((p) => p.unique_id === cardInstance.printingId)
      || { image_url: cardInstance.imageUrl, foiling: cardInstance.foiling };
    return {
      card,
      printing,
      zone: 'pile',
      entryIndex: idx,
      _instance: cardInstance,
    };
  };

  // --- CardGrid wiring (shared WebGL foil context for the pile grid) ---

  // Multi-select derived state for a tile, shared by getItemCellStyle,
  // renderTile and renderFooter so the selected/ineligible computation lives in
  // one place. Without an active multiSelect session everything is selectable.
  _selectionState(entry) {
    const { multiSelect } = this.props;
    const id = entry._instance.id;
    const isSelected = !!multiSelect && multiSelect.selectedIds.includes(id);
    const isIneligible = !!multiSelect
      && Array.isArray(multiSelect.eligibleIds)
      && !multiSelect.eligibleIds.includes(id)
      && !isSelected;
    return { isSelected, isIneligible };
  }

  getItemKey = (entry) => entry._instance.id;

  getItemFoil = (entry) => {
    const foiling = entry.printing?.foiling || 'S';
    if (!isFoilFinish(foiling)) return null;
    return { imageUrl: entry.printing?.image_url || '', zones: foilZonesForCard(entry.card) };
  };

  // Ineligible tiles (outside a multi-select allowlist) dim + desaturate. The
  // selection ring is drawn by DeckCardTile's isSelected; the graying lives on
  // the cell so it applies to the footer hint too (matching the legacy cell).
  getItemCellStyle = (entry) => {
    const { isIneligible } = this._selectionState(entry);
    return isIneligible ? { opacity: 0.35, filter: 'saturate(0.4)' } : {};
  };

  renderTile = (entry, { imageHidden, onTilt }) => {
    const {
      onTakeToHand, onCardContextMenu, onTokenSpawn, readOnly, pickMode = null, multiSelect = null,
    } = this.props;
    const isTokenPile = !!this.props.pile.infinite;

    const { isSelected, isIneligible } = this._selectionState(entry);

    // Multi-select session: clicks toggle the selection; right-click on a
    // selected tile unselects it; tiles outside the eligible allowlist lock.
    const handleClick = multiSelect
      ? (isIneligible ? undefined : () => multiSelect.onToggle(entry._instance))
      : isTokenPile && onTokenSpawn
        ? () => onTokenSpawn(entry._instance)
        : (readOnly ? undefined : () => onTakeToHand(entry._instance));
    // Pick mode strips the right-click affordance. The dialog is locked to
    // the pick action while an effect (e.g. Imposter Mask) is mid-resolution.
    const handleContextMenu = multiSelect
      ? (e) => {
          e.preventDefault();
          if (isSelected) multiSelect.onToggle(entry._instance);
        }
      : (isTokenPile || pickMode?.disableContextMenu)
        ? undefined
        : (readOnly ? undefined : (e) => {
            e.preventDefault();
            onCardContextMenu?.(entry._instance, e);
          });

    return (
      <div className="relative h-full w-full">
        <DeckCardTile
          entry={entry}
          isSelected={isSelected}
          imageHidden={imageHidden}
          onTilt={onTilt}
          onClick={handleClick}
          onHoverChange={(hovered) => this.setState({ hoveredEntry: hovered ? entry : null })}
          onContextMenu={handleContextMenu}
        />
        {isSelected && (
          <button
            type="button"
            aria-label={t('Unselect {name}', { name: entry.card.name })}
            title={t('Unselect')}
            className="absolute z-10 flex items-center justify-center font-bold"
            style={{
              top: '-0.4cqw',
              right: '-0.4cqw',
              width: '1.5cqw',
              height: '1.5cqw',
              borderRadius: '50%',
              background: 'rgba(20,16,10,0.96)',
              color: TEXT_PRIMARY,
              border: `1px solid ${GOLD} 0.7)`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.7)',
              fontSize: '0.75cqw',
              lineHeight: 1,
            }}
            onClick={(e) => {
              e.stopPropagation();
              multiSelect.onToggle(entry._instance);
            }}
          >
            ✕
          </button>
        )}
      </div>
    );
  };

  // Per-tile footer only during multi-select, where the line carries
  // per-card state (Selected / Not selectable). Static action hints live
  // in the dialog-level hint bar instead of repeating under every tile.
  renderFooter = (entry) => {
    const { isSelected, isIneligible } = this._selectionState(entry);
    return (
      <div className="mt-1 text-[0.625cqw] text-center truncate" style={{ color: TEXT_MUTED }}>
        {isSelected ? t('Selected · Right-click to undo') : (isIneligible ? t('Not selectable') : t('Click → select'))}
      </div>
    );
  };

  render() {
    const {
      pile, query, onQueryChange, onClose,
      readOnly, tabs, onSelectTab,
      // Optional pick-mode override. Set when an in-flight card effect
      // is using this dialog to ask the player to pick from a pile
      // (e.g. Imposter's Mask ability). Shape:
      //   { title, hint, disableContextMenu }
      // `title` replaces the pile-name header. `hint` replaces the
      // per-card click hint footer. `disableContextMenu: true` strips
      // the right-click handler so the only available action is the
      // pick itself.
      pickMode = null,
      // Optional multi-select session (pileMultiSelect.js). Clicking
      // a tile toggles its selection (highlight + ✕ badge); right-
      // clicking a selected tile unselects; the Accept button in the
      // footer commits. `eligibleIds` (when set) dims and locks every
      // other tile. Shape:
      //   { title, hint, confirmLabel, selectedIds, eligibleIds,
      //     max, onToggle(card), onConfirm() }
      multiSelect = null,
    } = this.props;
    const { typeFilters, elementFilters, sortBy, sortDir } = this.state;

    if (!pile) return null;

    const showTabs = Array.isArray(tabs) && tabs.length > 1;

    // Atlas piles only contain sites, so type filters would all be the same.
    // Show element filters instead. Spellbook and Cemetery use type filters.
    // Token piles skip filters entirely. All entries are tokens so filtering
    // by type/element adds no value and just takes up space.
    const isAtlas = pile.name === 'Atlas';
    const isTokenPile = !!pile.infinite;

    const lowerQuery = query.toLowerCase();
    const entries = pile.cards
      .map((c, i) => this.buildEntry(c, i))
      .filter(Boolean)
      .filter((e) => !lowerQuery || e.card.name.toLowerCase().includes(lowerQuery))
      .filter((e) => isTokenPile || isAtlas || typeFilters.size === 0 || typeFilters.has(e.card.type))
      .filter((e) => isTokenPile || !isAtlas || elementFilters.size === 0
        || e.card.elements?.some((el) => elementFilters.has(el.name)))
      .sort(makeCardComparator(sortBy, (e) => e.card, sortDir));

    return (
      <div className="responsive-dialog-overlay" onClick={onClose}>
        <div
          className="responsive-dialog-panel"
          style={{
            ...TEXTURED_PANEL_STYLE,
            borderRadius: 'var(--radius-xl)',
            // Fixed design size on the 1600×900 canvas. 1100×640 → cqw/cqh.
            // The .app-canvas always fits inside the viewport (uiScale's
            // clampedFitScale guarantees this), so the panel is bounded by
            // the canvas itself; no separate viewport clamp is needed.
            width: '68.75cqw',
            height: '71.11cqh',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <FourCorners />

          {/* Pile-switcher tabs (Banished: P1 / P2). Only renders when
              the host passes 2+ sibling piles via the `tabs` prop.
              Shared pill-in-bar tab pattern (TAB_BAR_STYLE container). */}
          {/* Header. Title + count + optional pile tabs + search + close.
              Always the first row so the close button sits at the panel's
              top-right corner regardless of whether tabs render. */}
          <div className="flex items-center gap-3 p-4">
            <h2 className="text-lg font-semibold arena-heading" style={{ color: TEXT_PRIMARY }}>
              {multiSelect?.title || pickMode?.title || t(pile.name)}
            </h2>
            <span className="text-sm tabular-nums" style={{ color: TEXT_MUTED }}>
              {multiSelect
                ? t('{n} / {max} selected', { n: multiSelect.selectedIds.length, max: multiSelect.max })
                : (pile.cards.length === 1 ? t('1 card') : t('{n} cards', { n: pile.cards.length }))}
            </span>
            {showTabs && (
              <TabBar
                className="inline-flex items-center gap-0.5"
                style={TAB_BAR_STYLE}
                activeIndex={tabs.findIndex((tab) => tab.pile?.id === pile.id)}
              >
                {tabs.map((tab) => {
                  const isActive = tab.pile?.id === pile.id;
                  return (
                    <button
                      key={tab.pile?.id || tab.label}
                      data-tab-item
                      type="button"
                      className="px-3 py-1 text-xs font-semibold"
                      style={isActive ? TAB_ACTIVE_TEXT : TAB_INACTIVE}
                      onClick={() => { if (!isActive && onSelectTab) onSelectTab(tab.pile); }}
                    >
                      {tab.label}
                      {tab.readOnly ? (
                        <span className="ml-1.5 opacity-70" style={{ color: TEXT_MUTED, fontWeight: 500 }}>{t('(view-only)')}</span>
                      ) : null}
                    </button>
                  );
                })}
              </TabBar>
            )}
            <div className="ml-auto flex items-center gap-2">
              <div className="flex items-center gap-0.5" aria-label={t('Sort cards by')}>
                <span className="text-[0.625cqw] mr-1" style={{ color: TEXT_MUTED }}>
                  {t('Sort')}
                </span>
                {CARD_SORT_OPTIONS.map((opt) => {
                  const active = sortBy === opt.value;
                  const isCost = opt.value === CARD_SORT_BY_COST;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      className="filter-chip"
                      data-active={active ? 'true' : 'false'}
                      aria-label={
                        active && isCost
                          ? (sortDir === CARD_SORT_DIR_DESC ? t('Sorted by Cost descending; click to reverse') : t('Sorted by Cost ascending; click to reverse'))
                          : t('Sort by {label}', { label: t(opt.label) })
                      }
                      aria-pressed={active}
                      onClick={() => this.handleSortByChange(opt.value)}
                    >
                      {t(opt.label)}
                      {/* Arrow slot always rendered on the Cost chip so the
                          row doesn't shift when the arrow appears/disappears. */}
                      {isCost && (
                        <span aria-hidden="true" style={{ opacity: active ? 1 : 0 }}>
                          {' '}{sortDir === CARD_SORT_DIR_DESC ? '↓' : '↑'}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <input
                type="search"
                placeholder={t('Search cards...')}
                value={query}
                onInput={(e) => onQueryChange(e.target.value)}
                className="px-3 py-1.5 text-sm outline-none"
                style={{ ...INPUT_STYLE, borderRadius: 'var(--radius-md)', color: TEXT_PRIMARY, width: '13.75cqw' }}
              />
              <CloseButton onClick={onClose} />
            </div>
          </div>
          <Divider />

          {/* Filter row. Element filters for Atlas (sites only),
              type filters for Spellbook / Cemetery. Hidden for the
              token pile since all entries share the same purpose and
              filtering by type/element adds no value. Chips use the
              shared .filter-chip class (same as CardFilterBar and the
              sort chips above) instead of a bespoke toggle style. */}
          {!isTokenPile && (
          <>
          <div className="flex items-center gap-0.5 px-4 py-2">
            {isAtlas ? (
              <>
                <span className="text-[0.625cqw] mr-2" style={{ color: TEXT_MUTED }}>
                  {t('Element')}
                </span>
                <button
                  type="button"
                  className="filter-chip"
                  data-active={elementFilters.size === 0 ? 'true' : 'false'}
                  onClick={this.clearElements}
                >
                  {t('All')}
                </button>
                {ELEMENTS.map((el) => (
                  <button
                    key={el}
                    type="button"
                    className="filter-chip inline-flex items-center gap-1"
                    data-active={elementFilters.has(el) ? 'true' : 'false'}
                    onClick={() => this.toggleElement(el)}
                  >
                    <SorceryElementIcon element={el} className="size-3" />
                    {t(el)}
                  </button>
                ))}
              </>
            ) : (
              <>
                <span className="text-[0.625cqw] mr-2" style={{ color: TEXT_MUTED }}>
                  {t('Type')}
                </span>
                <button
                  type="button"
                  className="filter-chip"
                  data-active={typeFilters.size === 0 ? 'true' : 'false'}
                  onClick={this.clearTypes}
                >
                  {t('All')}
                </button>
                {TYPES.map((type) => (
                  <button
                    key={type}
                    type="button"
                    className="filter-chip"
                    data-active={typeFilters.has(type) ? 'true' : 'false'}
                    onClick={() => this.toggleType(type)}
                  >
                    {t(type)}
                  </button>
                ))}
              </>
            )}
            <span className="ml-auto text-xs tabular-nums" style={{ color: TEXT_MUTED }}>
              {t('Showing {n} of {total}', { n: entries.length, total: pile.cards.length })}
            </span>
          </div>
          <Divider />
          </>
          )}

          {/* Card grid. Rendered through the shared CardGrid so every foil
              tile draws through the ONE WebGL context (no per-tile FoilCanvas
              churn). CardGrid owns its own scroll container. */}
          <div className="flex-1 flex min-h-0 py-2 relative">
            {/* CardGrid stays mounted across filter changes (incl. transient
                empty results) so its WebGL foil context isn't torn down and
                rebuilt. The empty message overlays it when nothing matches. */}
            <CardGrid
              items={entries}
              gridSize={6}
              getItemKey={this.getItemKey}
              getItemFoil={this.getItemFoil}
              getItemCellStyle={this.getItemCellStyle}
              renderTile={this.renderTile}
              renderFooter={multiSelect ? this.renderFooter : undefined}
              measureFooter={multiSelect ? (
                <div className="mt-1 text-[0.625cqw]" style={{ lineHeight: 1.3 }}>&nbsp;</div>
              ) : undefined}
            />
            {entries.length === 0 ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
                <span className="text-sm" style={{ color: TEXT_BODY }}>
                  {pile.cards.length === 0 ? t('Pile is empty') : t('No cards match')}
                </span>
                {pile.cards.length > 0 ? (
                  <span className="text-xs" style={{ color: TEXT_MUTED }}>
                    {t('Clear the search or filters to see all {n} cards', { n: pile.cards.length })}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>

          {/* Hint bar. One muted line for the whole pile instead of the
              same instruction repeated under every tile. Multi-select has
              its own footer with per-card state, so this bar yields to it. */}
          {!multiSelect && !readOnly && (
            <>
              <Divider />
              <div className="flex items-center justify-center px-4 py-2 text-xs" style={{ color: TEXT_MUTED }}>
                {pickMode?.hint
                  || (isTokenPile
                    ? t('Click → place on field · Space → inspect')
                    : t('Click → hand · Right-click → menu · Space → inspect'))}
              </div>
            </>
          )}

          {/* Multi-select footer. Running count, hint, and the
              explicit Accept. Nothing commits until this click;
              closing the dialog instead aborts the whole session.
              `minSelect` (default = max) lets "up to N" / "any number"
              sessions accept with fewer than max picks. */}
          {multiSelect && (() => {
            const minSelect = multiSelect.minSelect ?? multiSelect.max;
            const count = multiSelect.selectedIds.length;
            const ready = count >= minSelect && count <= multiSelect.max;
            return (
            <>
            <Divider />
            <div className="flex items-center gap-3 px-4 py-3">
              <span className="text-sm" style={{ color: TEXT_MUTED }}>
                {multiSelect.hint}
              </span>
              <div className="ml-auto flex items-center gap-3">
                <span className="text-sm font-semibold tabular-nums" style={{ color: TEXT_PRIMARY }}>
                  {count} / {multiSelect.max}
                </span>
                <button
                  type="button"
                  className="px-5 py-2 text-sm font-semibold active:scale-[0.96] transition-transform disabled:opacity-40 disabled:cursor-not-allowed"
                  style={ready ? GOLD_BTN : {
                    background: 'rgba(60,50,34,0.9)',
                    color: TEXT_MUTED,
                    border: `1px solid ${GOLD} 0.35)`,
                    borderRadius: 'var(--radius-md)',
                  }}
                  disabled={!ready}
                  onClick={() => multiSelect.onConfirm()}
                >
                  {multiSelect.confirmLabel || t('Accept')}
                </button>
              </div>
            </div>
            </>
            );
          })()}
        </div>
        {this.state.inspectedEntry ? (
          <CardInspector
            card={this.state.inspectedEntry.card}
            imageUrl={this.state.inspectedEntry.printing?.image_url}
            rarity={this.state.inspectedEntry.card?.rarity}
            foiling={this.state.inspectedEntry.printing?.foiling}
            onClose={() => this.setState({ inspectedEntry: null })}
          />
        ) : null}
      </div>
    );
  }
}
